<script setup lang="ts">
import { ref, provide, watch, type Ref, watchEffect } from 'vue'
import { Tabs } from '@ark-ui/vue'
import UiText from './UiText.vue'
import UiIcon, { UiIconProps } from './UiIcon.vue'
import UiLockedBadge from './UiLockedBadge.vue'

export interface UiTabsLayoutItemMeta {
    value: string
    label: string
    description?: string
    locked?: boolean
    disabled?: boolean
    hideDescriptionUnlessSelected?: boolean
    icon?: UiIconProps['name']
}

export interface UiTabsLayoutProps {
    heading?: string
    isMobileSelectorOpen?: boolean
    defaultValue?: string
    sticky?: boolean
}

const props = withDefaults(defineProps<UiTabsLayoutProps>(), {
    heading: '',
    isMobileSelectorOpen: false,
    sticky: false,
})

const model = defineModel<string>()

const items: Ref<UiTabsLayoutItemMeta[]> = ref([])

function registerItem(item: UiTabsLayoutItemMeta) {
    if (!items.value.find((i) => i.value === item.value)) {
        items.value.push(item)
    } else {
        items.value = items.value.map((i) => (i.value === item.value ? item : i))
    }
}

function unregisterItem(value: string) {
    items.value = items.value.filter((i) => i.value !== value)
}

provide('UiTabsLayout:registerItem', registerItem)
provide('UiTabsLayout:unregisterItem', unregisterItem)

watchEffect(() => {
    if (!model.value && items.value.length > 0) {
        model.value = props.defaultValue || items.value.find(i => !isDisabled(i))?.value
    }
})

function isDisabled(item: UiTabsLayoutItemMeta) {
    return item.disabled ?? item.locked
}
</script>

<template>
    <Tabs.Root v-model="model" orientation="vertical"
        class="w-full h-[calc(100dvh-var(--safe-area-top,0px)-var(--safe-area-bottom,0px))]">
        <div class="w-full h-full md:grid md:grid-cols-[384px_1fr]">
            <aside class="hidden md:block bg-se-white md:border-r md:border-se-gray-300 py-8 px-4 md:px-8 
                max-md:data-[mobile-menu=open]:block max-md:sticky max-md:inset-0 
                max-md:z-50 max-md:top-[var(--safe-area-top,0px)] 
                max-md:h-[calc(100dvh-var(--safe-area-top,0px)-var(--safe-area-bottom,0px))]
                max-md:overflow-y-auto
                max-md:se-scroll-lock
                data-sticky:top-[var(--safe-area-top)] data-sticky:left-0 data-sticky:max-h-[calc(100dvh-var(--safe-area-top,0px)-var(--safe-area-bottom,0px))] data-sticky:overflow-y-auto
                " :data-mobile-menu="props.isMobileSelectorOpen ? 'open' : 'closed'"
                :data-sticky="props.sticky ? true : false">
                <div class="
                flex flex-col gap-8
                ">
                    <slot name="before-list" />
                    <div class="flex flex-col gap-2">
                        <UiText v-if="props.heading" as="h2" variant="h6" class="text-se-gray-500">{{ props.heading }}
                        </UiText>
                        <Tabs.List class="flex flex-col gap-3">
                            <Tabs.Trigger v-for="item in items" :key="item.value" :value="item.value"
                                :disabled="isDisabled(item)"
                                class="w-full text-left rounded-sm border-1 border-se-gray-300 bg-white p-4 hover:bg-se-gray-50 focus:outline-none data-selected:border-se-dark cursor-pointer data-disabled:opacity-60 data-disabled:cursor-not-allowed">
                                <div class="flex flex-col items-stretch gap-2">
                                    <div class="flex items-center gap-2">
                                        <UiIcon :name="item.icon" v-if="item.icon" class="w-5 h-5 mt-0.5" />
                                        <UiText as="h6" variant="h6" class="flex-1 font-semibold">{{ item.label }}
                                        </UiText>
                                        <UiLockedBadge v-if="item.locked" />
                                    </div>
                                    <UiText v-if="item.description" as="p" variant="h6" :class="{
                                        'hidden': Boolean(item.hideDescriptionUnlessSelected && model !== item.value)
                                    }">
                                        {{ item.description }}
                                    </UiText>
                                </div>
                            </Tabs.Trigger>
                        </Tabs.List>
                    </div>
                    <slot name="after-list" />
                </div>
            </aside>

            <main class="h-full py-6 md:py-8 px-4 md:px-8 bg-se-secondary-background min-w-0 min-h-0 overflow-y-auto">
                <div class="tab-content">
                    <slot />
                </div>
            </main>
        </div>
    </Tabs.Root>
</template>
